/*
 * FICHIER: SimplePollService.js
 * BUT: Service de gestion des sondages avec nouvelle structure Strapi
 * 
 * NOUVELLE STRUCTURE :
 * - Sondage avec clubs participants (Many to Many)
 * - Vote avec IP + club + sondage (1 vote par IP par 24h)
 * - Retourne null si API indisponible ou aucun sondage disponible
 * 
 * FONCTIONNALITÉS :
 * - Récupération du sondage actif avec ses clubs (ou dernier sondage terminé)
 * - Soumission de votes avec limitation IP (24h)
 * - Récupération des résultats en temps réel
 * - Vérification de l'état de vote de l'utilisateur
 * - Calcul du temps restant avant le prochain vote
 * 
 * PATTERN :
 * - Service avec méthodes async/await
 * - Retourne null/erreur si API indisponible (pas de données de secours)
 * - Utilisation de localStorage pour la persistance côté client
 * - Export singleton pour une utilisation globale
 */

import * as strapiApi from '../api/strapi';

const POLL_RESULTS_CACHE_TTL_MS = 15000;
const pollResultsCache = new Map();

/**
 * SERVICE PRINCIPAL: Gestion complète des sondages avec nouvelle structure
 */
export const SimplePollService = {
  
  /**
   * RÉCUPÉRATION DU SONDAGE ACTIF
   * 
   * Récupère le sondage actif depuis Strapi avec ses clubs participants.
   * Si aucun sondage actif, retourne le dernier sondage terminé.
   * Si aucun sondage disponible ou API indisponible, retourne null.
   * 
   * @returns {Object} Objet avec success et poll/error
   */
  getCurrentPoll: async () => {
    try {
      // TENTATIVE API: Récupération depuis Strapi
      const sondage = await strapiApi.getSondageActif();
      
      if (sondage) {
        // FORMATAGE pour l'interface compatible avec l'ancien système
        // Accepter le sondage même s'il n'a pas de clubs (pour tester le titre au minimum)
        const clubs = sondage.clubs && sondage.clubs.length > 0 ? sondage.clubs : [];
        
        const formattedPoll = {
          id: sondage.id,
          documentId: sondage.documentId || sondage.id,
          title: sondage.Titre,
          description: sondage.Description,
          statut: sondage.Statut || sondage.statut,
          startDate: sondage.Date_Debut || sondage.DateDebut || sondage.date_debut,
          endDate: sondage.Date_Fin || sondage.DateFin || sondage.date_fin,
              question: {
            text: sondage.Titre, // Le titre sert de question
            options: clubs.map(club => {
              // Mapping identique à Clubs.js
              const data = club.attributes || club;
              let logo = null;
              if (data.Image) {
                const imagesData = Array.isArray(data.Image) ? data.Image : (data.Image?.data || []);
                if (imagesData.length > 0) {
                  const img = imagesData[0];
                  const url = img?.attributes?.url || img?.url;

                  if (url) {
                    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                    const baseUrl = isLocalhost ? 'http://localhost:1337' : 'https://api.offisport.com';
                    logo = url.startsWith('http') ? url : `${baseUrl}${url}`;
                  }
                }
              }
              return {
                id: club.id,
                text: data.Nom || data.nom,
                logo: logo
              };
            })
          }
        };
        
        // Si pas de clubs, le sondage n'est pas valide
        if (clubs.length === 0) {
          console.log('⚠️ Sondage Strapi sans clubs, sondage non valide');
          return { success: false, poll: null };
        }
        
        console.log('✅ Sondage Strapi récupéré:', formattedPoll);
        return { success: true, poll: formattedPoll, raw: sondage };
      } else {
        // Aucun sondage trouvé (ni actif ni terminé)
        console.log('⚠️ Aucun sondage trouvé (ni actif ni terminé)');
        // DIAGNOSTIC: Analyser pourquoi aucun sondage n'est trouvé
        await strapiApi.diagnoseSondages();
        return { success: false, poll: null };
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du sondage:', error);
      // Ne plus utiliser le fallback, retourner null si erreur API
      return { success: false, poll: null, error: error.message };
    }
  },

  /**
   * SOUMISSION D'UN VOTE
   * 
   * Soumet un vote pour un club avec validation de la limitation temporelle (24h).
   * Utilise l'API Strapi pour enregistrer le vote ou localStorage en fallback.
   * 
   * @param {string} pollDocumentId - documentId du sondage (stable, ne change pas lors des modifications)
   * @param {number} optionIndex - Index de l'option choisie
   * @param {string} optionText - Texte de l'option choisie
   * @returns {Object} Résultat de la soumission
   */
  submitVote: async (pollDocumentId, optionIndex, optionText, captchaToken) => {
    // DÉSACTIVER LE BLOCAGE EN MODE DÉVELOPPEMENT
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

    // VÉRIFICATION TEMPORELLE: Limitation 24h par navigateur (localStorage)
    // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
    if (!isDevelopment) {
      const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
      const lastVoteDate = localStorage.getItem(lastVoteKey);
    
    if (lastVoteDate) {
      const hoursSinceLastVote = (Date.now() - new Date(lastVoteDate).getTime()) / (1000 * 60 * 60);
      if (hoursSinceLastVote < 24) {
        return {
          success: false,
          error: `Vous avez déjà voté. Vous pourrez voter à nouveau dans ${Math.ceil(24 - hoursSinceLastVote)} heures.`
        };
      }
      }
    } else {
      console.log('🔧 Mode développement : limitation temporelle désactivée');
    }

    try {
      // RÉCUPÉRATION DU SONDAGE pour obtenir les clubs
      const sondage = await strapiApi.getSondageActif();
      
      if (!sondage || !sondage.clubs || optionIndex >= sondage.clubs.length) {
        throw new Error('Sondage ou option invalide');
      }

      // Utiliser les documentId pour les relations Strapi v5
      const sondageDocumentId = sondage.documentId || sondage.id;
      const clubDocumentId = sondage.clubs[optionIndex].documentId || sondage.clubs[optionIndex].id;

      console.log('🔍 Données du sondage pour vote:', {
        sondageId: sondage.id,
        sondageDocumentId,
        clubId: sondage.clubs[optionIndex].id,
        clubDocumentId,
        clubNom: sondage.clubs[optionIndex].Nom || sondage.clubs[optionIndex].nom
      });

      // COUCHE 3 : obtenir un ticket de vote signé (HMAC, usage unique) avant de voter.
      const ticket = await strapiApi.getVoteTicket(sondageDocumentId);
      if (!ticket) {
        return { success: false, error: 'Impossible d\'initialiser le vote, rechargez la page.' };
      }

      // TENTATIVE API: Soumission via Strapi avec documentId
      const response = await strapiApi.submitVoteForClub(sondageDocumentId, clubDocumentId, captchaToken, ticket);
      
      if (response.success) {
        // Invalider le cache des résultats après un vote réussi
        pollResultsCache.delete(String(pollDocumentId));

        // SUCCÈS API: Enregistrer localement pour la limitation
        // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
        const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
        localStorage.setItem(lastVoteKey, new Date().toISOString());
        localStorage.setItem(`poll_choice_${pollDocumentId}`, optionText);
        console.log('✅ Vote enregistré via API Strapi');
        return { success: true };
      } else {
        // Erreur API - peut être due à un vote déjà effectué
        console.error('Erreur API:', response.error);
        
        // Si l'erreur indique un vote déjà effectué, considérer comme succès
        if (response.error && (
          response.error.includes('déjà voté') || 
          response.error.includes('already voted') ||
          response.error.includes('400') ||
          response.error.includes('Bad Request')
        )) {
          // Le vote existe déjà, enregistrer localement
          const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
          localStorage.setItem(lastVoteKey, new Date().toISOString());
          localStorage.setItem(`poll_choice_${pollDocumentId}`, optionText);
          console.log('⚠️ Vote déjà effectué côté serveur, synchronisation locale');
          return { success: true, alreadyVoted: true };
        }
        
        throw new Error(response.error);
      }
    } catch (error) {
      // Erreur lors de la soumission
      console.error('Erreur lors de la soumission du vote:', error.message);
      
      // Si l'erreur 400 indique un vote déjà effectué
      if (error.message && (
        error.message.includes('400') ||
        error.message.includes('Bad Request') ||
        error.message.includes('déjà voté')
      )) {
        // Le vote existe probablement déjà côté serveur, enregistrer localement
        const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
        localStorage.setItem(lastVoteKey, new Date().toISOString());
        localStorage.setItem(`poll_choice_${pollDocumentId}`, optionText);
        console.log('⚠️ Vote probablement déjà effectué, synchronisation locale');
        return { success: true, alreadyVoted: true };
      }
      
      return { success: false, error: error.message };
    }
  },

  /**
   * RÉCUPÉRATION DES RÉSULTATS
   * 
   * Récupère les résultats du sondage depuis l'API Strapi.
   * Si l'API est indisponible ou aucun résultat, retourne un objet vide.
   * 
   * @param {string} pollId - ID du sondage
   * @returns {Object} Résultats avec votes et pourcentages (ou {results: [], totalVotes: 0})
   */
  getPollResults: async (pollOrId) => {
    let documentId = null;
    if (typeof pollOrId === 'object' && pollOrId !== null) {
      documentId = pollOrId.documentId || pollOrId.id;
    } else {
      // pollOrId est un id numérique, il faut aller chercher le sondage actif
      try {
        const sondage = await strapiApi.getSondageActif();
        documentId = sondage?.documentId || sondage?.id || pollOrId;
      } catch (e) {
        documentId = pollOrId;
      }
    }

    const cacheKey = String(documentId || '');
    const cachedEntry = pollResultsCache.get(cacheKey);
    if (cachedEntry && (Date.now() - cachedEntry.timestamp) < POLL_RESULTS_CACHE_TTL_MS) {
      return cachedEntry.data;
    }

    try {
      // TENTATIVE API: Récupération des vrais résultats
      const results = await strapiApi.getVoteResults(documentId);
      const normalizedResults = (results && results.results)
        ? results
        : { results: [], totalVotes: 0 };

      pollResultsCache.set(cacheKey, {
        data: normalizedResults,
        timestamp: Date.now()
      });

      if (normalizedResults.results.length > 0) {
        console.log('✅ Résultats Strapi récupérés:', normalizedResults);
      } else {
        console.log('⚠️ Aucun résultat trouvé pour ce sondage');
      }
      return normalizedResults;
    } catch (error) {
      console.error('❌ Erreur API lors de la récupération des résultats:', error);
      // En cas d'échec API, réutiliser le dernier résultat connu si disponible
      if (cachedEntry?.data) {
        return cachedEntry.data;
      }
      // Retourner des résultats vides si l'API est indisponible
      return { results: [], totalVotes: 0 };
    }
  },

  /**
   * VÉRIFICATION DE VOTE RÉCENT
   * 
   * Vérifie si l'utilisateur a voté dans les 24 dernières heures.
   * Utilise localStorage ET l'API Strapi pour une vérification complète.
   * 
   * @param {string} pollDocumentId - documentId du sondage (stable, ne change pas lors des modifications)
   * @returns {boolean} True si l'utilisateur a déjà voté récemment
   */
  hasVotedInLast24Hours: async (pollDocumentId) => {
    // VÉRIFICATION LOCALE (localStorage)
    // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
    const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
    const lastVoteDate = localStorage.getItem(lastVoteKey);
    
    if (lastVoteDate) {
      const hoursSinceLastVote = (Date.now() - new Date(lastVoteDate).getTime()) / (1000 * 60 * 60);
      if (hoursSinceLastVote < 24) {
        return true;
      }
    }

    // VÉRIFICATION SERVEUR (endpoint sécurisé /votes/check, IP lue côté backend).
    // Fiable même si le cache/localStorage a été vidé ou si l'utilisateur change de navigateur.
    try {
      const status = await strapiApi.getVoteStatusForSondage(pollDocumentId);
      if (status.hasVoted) {
        // Re-synchroniser le localStorage avec la VRAIE date du vote serveur
        // pour que le temps restant affiché soit exact même après vidage du cache.
        const realDate = status.dateReponse ? new Date(status.dateReponse) : new Date();
        localStorage.setItem(lastVoteKey, realDate.toISOString());
        if (status.clubNom) {
          localStorage.setItem(`poll_choice_${pollDocumentId}`, status.clubNom);
        }
        return true;
      }
    } catch (error) {
      console.debug('Vérification serveur échouée:', error.message);
      // En cas d'erreur serveur, on se base uniquement sur localStorage
    }

    return false;
  },

  /**
   * RÉCUPÉRATION DU CHOIX UTILISATEUR
   * 
   * Récupère le choix précédent de l'utilisateur pour le mettre
   * en évidence dans les résultats.
   * 
   * @param {string} pollDocumentId - documentId du sondage (stable, ne change pas lors des modifications)
   * @returns {string|null} Texte de l'option choisie ou null
   */
  getUserChoice: async (pollDocumentId) => {
    // VÉRIFICATION LOCALE (localStorage)
    // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
    const localChoice = localStorage.getItem(`poll_choice_${pollDocumentId}`);
    if (localChoice) {
      return localChoice;
    }

    // VÉRIFICATION SERVEUR (endpoint sécurisé /votes/check, IP lue côté backend).
    // Permet de retrouver le club voté même si le cache/localStorage a été vidé.
    try {
      const status = await strapiApi.getVoteStatusForSondage(pollDocumentId);
      if (status.hasVoted && status.clubNom) {
        // Synchroniser le localStorage (choix + date réelle du vote).
        localStorage.setItem(`poll_choice_${pollDocumentId}`, status.clubNom);
        if (status.dateReponse) {
          localStorage.setItem(`poll_last_vote_${pollDocumentId}`, new Date(status.dateReponse).toISOString());
        }
        return status.clubNom;
      }
    } catch (error) {
      console.debug('Récupération choix serveur échouée:', error.message);
    }

    return null;
  },

  /**
   * CALCUL DU TEMPS RESTANT
   * 
   * Calcule le nombre d'heures restantes avant que l'utilisateur
   * puisse voter à nouveau.
   * 
   * @param {string} pollDocumentId - documentId du sondage (stable, ne change pas lors des modifications)
   * @returns {number} Nombre d'heures restantes (0 si peut voter)
   */
  getTimeUntilNextVote: (pollDocumentId) => {
    // Utiliser documentId (stable) au lieu de id (peut changer lors des modifications)
    const lastVoteKey = `poll_last_vote_${pollDocumentId}`;
    const lastVoteDate = localStorage.getItem(lastVoteKey);
    
    if (!lastVoteDate) return 0;
    
    const hoursSinceLastVote = (Date.now() - new Date(lastVoteDate).getTime()) / (1000 * 60 * 60);
    return Math.max(0, 24 - hoursSinceLastVote);
  },

  /**
   * Récupère le gagnant du dernier sondage terminé (pour affichage sous le bloc "à venir").
   * @returns {Promise<Object|null>} { pollTitle, winnerText, winnerVotes, totalVotes } ou null
   */
  getLastFinishedPollWinner: async () => {
    try {
      const dernier = await strapiApi.getDernierSondageTermine();
      if (!dernier) return null;
      const results = await strapiApi.getVoteResults(dernier.documentId || dernier.id);
      if (!results?.results?.length) return null;
      const winner = results.results.reduce((best, r) => (!best || r.votes > best.votes ? r : best), null);
      return {
        pollTitle: dernier.Titre || 'Sondage précédent',
        winnerText: winner.text,
        winnerVotes: winner.votes,
        totalVotes: results.totalVotes || 0
      };
    } catch (error) {
      console.debug('getLastFinishedPollWinner:', error?.message);
      return null;
    }
  }
};

// EXPORT PAR DÉFAUT pour import simplifié
export default SimplePollService; 